import React, {Fragment, useRef} from 'react';
import {Dialog, Transition} from '@headlessui/react';
import {XMarkIcon} from '@heroicons/react-v2/24/solid';
import _ from 'lodash';

import CloseButton from './CloseButton';
import {cx} from '../util';

export const Close = ({className = '', onClick = _.noop, disabled = false}) => (
  <CloseButton
    className={cx('absolute top-4 right-4 w-5', className)}
    onClick={onClick}
    disabled={disabled}
  />
);

export default function ModalNew({
  header,
  isOpen,
  modalClassName,
  modalStyle,
  overlayClassName,
  showCloseButton,
  centerVertically = true,
  children,
  onClose = () => {},
  onCloseFinish = _.noop,
  initialFocus,
  className,
}) {
  const titleRef = useRef(null);

  return (
    <Transition.Root appear show={isOpen} as={Fragment} afterLeave={onCloseFinish}>
      {/* Above the sidebar shell (sidebar z-30, chat drawer z-50), so the
          overlay dims the whole viewport including the nav. */}
      <Dialog as="div" className={cx('relative z-[60]', className)} onClose={onClose} initialFocus={initialFocus || (header ? titleRef : undefined)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className={cx('fixed inset-0 bg-black/40', overlayClassName)} />
        </Transition.Child>

        <div className="fixed inset-0 z-[60] w-screen overflow-y-auto">
          <div className={cx('flex min-h-full items-end justify-center p-4 text-center sm:p-0', centerVertically ? 'sm:items-center' : 'sm:items-start')}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={cx(
                  'relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-full sm:my-8',
                  modalStyle ? '' : 'px-4 pb-4 pt-5 p-6 max-w-lg',
                  modalClassName,
                )}
                style={modalStyle}
              >
                {showCloseButton && (
                  <button
                    type="button"
                    className="absolute top-2 right-2 p-2 rounded-lg text-gray-400 bg-white/70 hover:text-gray-600 hover:bg-gray-50 transition duration-150"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                    <span className="sr-only">Close</span>
                  </button>
                )}
                {header && (
                  <Dialog.Title
                    ref={titleRef}
                    tabIndex={-1}
                    aria-live="polite"
                    aria-atomic="true"
                    className="mb-4 flex justify-center text-lg font-semibold text-gray-900 tracking-tight"
                  >
                    {header}
                  </Dialog.Title>
                )}
                {children}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
